import React, { useState, useEffect } from 'react';

import {
  useTranslate,
  SimpleShowLayout,
  useNotify,
  useRedirect,
} from 'react-admin';
import {
  Divider,
  Button,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import SectionTitle from '../SectionTitle';
import CustomSelect from '../CustomSelect';
import CustomCheckbox from '../CustomCheckbox';
import CustomTextArea from '../CustomTextArea';
import { updateResource } from '../../utils/requests';

import './styles.scss';

const ImmunotherapyTreatmentPreparatoryCallForm = ({
  readonly,
  data,
}: {
  readonly: boolean;
  data: any;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const { immunotherapy } = data.test;
  const preparatoryConsultationStep = data.steps.find(
    (step: any) => step.id === 'bookPreparatoryConsultation',
  );
  const { medications } = immunotherapy;

  const [firstMedicationId, setFirstMedicationId] = useState<string>(
    preparatoryConsultationStep?.consultationData?.medicationOrder?.length > 0
      ? preparatoryConsultationStep?.consultationData?.medicationOrder[0]
      : '',
  );
  const [secondMedicationId, setSecondMedicationId] = useState<string>(
    preparatoryConsultationStep?.consultationData?.medicationOrder?.length > 1
      ? preparatoryConsultationStep?.consultationData?.medicationOrder[1]
      : '',
  );

  // State for confirmation checkboxes
  const [treatmentProcessConfirmed, setTreatmentProcessConfirmed] =
    useState<boolean>(
      preparatoryConsultationStep?.consultationData
        ?.treatmentProcessConfirmed || false,
    );
  const [sideEffectsConfirmed, setSideEffectsConfirmed] = useState<boolean>(
    preparatoryConsultationStep?.consultationData?.sideEffectsConfirmed ||
      false,
  );
  const [contraindicationsConfirmed, setContraindicationsConfirmed] =
    useState<boolean>(
      preparatoryConsultationStep?.consultationData
        ?.contraindicationsConfirmed || false,
    );

  // State for note textarea
  const [note, setNote] = useState<string>(
    preparatoryConsultationStep?.consultationData?.note || '',
  );

  // State for form submission
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  // State for accordion expand/collapse
  const [isExpanded, setIsExpanded] = useState<boolean>(!readonly);

  // Clear errors whenever form state changes
  useEffect(() => {
    if (formErrors.length > 0) {
      setFormErrors([]);
    }
  }, [
    treatmentProcessConfirmed,
    sideEffectsConfirmed,
    contraindicationsConfirmed,
    firstMedicationId,
    secondMedicationId,
    note,
  ]);

  // Create options from medications array
  const medicationOptions = medications.map((medication: any) => ({
    value: medication.medicine.id,
    label: medication.medicine.name,
  }));

  const handleFirstMedicationChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedId = event.target.value;
    setFirstMedicationId(selectedId);

    // Automatically set the other dropdown to the remaining medication
    if (selectedId && medications.length === 2) {
      const otherMedication = medications.find(
        (med: any) => med.medicine.id !== selectedId,
      );
      if (otherMedication) {
        setSecondMedicationId(otherMedication.medicine.id);
      }
    } else if (!selectedId) {
      // If clearing the selection, also clear the other dropdown
      setSecondMedicationId('');
    }
  };

  const handleSecondMedicationChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedId = event.target.value;
    setSecondMedicationId(selectedId);

    // Automatically set the other dropdown to the remaining medication
    if (selectedId && medications.length === 2) {
      const otherMedication = medications.find(
        (med: any) => med.medicine.id !== selectedId,
      );
      if (otherMedication) {
        setFirstMedicationId(otherMedication.medicine.id);
      }
    } else if (!selectedId) {
      // If clearing the selection, also clear the other dropdown
      setFirstMedicationId('');
    }
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value);
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    // Validate all checkboxes are checked
    if (!treatmentProcessConfirmed) {
      errors.push(
        `${translate(
          'immunotherapy.preparatoryCall.treatmentProcessLabel',
        )} ${translate('ra.validation.required')}`,
      );
    }
    if (!sideEffectsConfirmed) {
      errors.push(
        `${translate(
          'immunotherapy.preparatoryCall.possibleSideEffectsLabel',
        )} ${translate('ra.validation.required')}`,
      );
    }
    if (!contraindicationsConfirmed) {
      errors.push(
        `${translate(
          'immunotherapy.preparatoryCall.contraindicationsLabel',
        )} ${translate('ra.validation.required')}`,
      );
    }

    // Validate medication dropdowns if there are 2 medications
    if (medications.length === 2) {
      if (!firstMedicationId) {
        errors.push(
          `${translate(
            'immunotherapy.preparatoryCall.medicationFor1stTimeIntake',
          )} ${translate('ra.validation.required')}`,
        );
      }
      if (!secondMedicationId) {
        errors.push(
          `${translate(
            'immunotherapy.preparatoryCall.medicationFor2ndTimeIntake',
          )} ${translate('ra.validation.required')}`,
        );
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    setFormErrors(errors);

    if (errors.length > 0) {
      return;
    }

    try {
      setIsLoading(true);
      const updates = {
        activeStepId: data.activeStepId,
        treatmentProcessConfirmed,
        sideEffectsConfirmed,
        contraindicationsConfirmed,
        note: note || null,
        // If firstMedicationId is not set, set it to the first medication in the list, and it's not set in case when there is only
        // one medication is assigned
        firstMedicationId: firstMedicationId || medications[0].medicine.id,
        ...(medications.length === 2 && {
          secondMedicationId,
        }),
      };

      await updateResource(`treatments/${data.id}`, updates);
      notify('tests.addReportSuccessMessage', { type: 'success' });
      redirect('/treatments');
    } catch (e) {
      notify('tests.addReportErrorMessage', { type: 'warning' });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <SimpleShowLayout>
      <Divider className="divider" />
      <div className="formWrapper">
        <div
          className="accordion-header"
          onClick={toggleExpanded}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
        >
          <SectionTitle
            title={translate('immunotherapy.preparatoryCall.title')}
          />
          <IconButton
            className="accordion-toggle-icon"
            size="small"
            aria-label="expand or collapse"
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
        {isExpanded && (
          <>
            <div className="treatmentBlock treatmentBlock-list">
              <div className="treatmentBlock-subtitle">
                {translate('immunotherapy.preparatoryCall.assignedMedications')}
              </div>
              {medications.map((medication: any) => (
                <div key={medication.id}>
                  <div className="treatmentBlock-text">
                    {medication.medicine.name}
                  </div>
                </div>
              ))}
            </div>
            {medications.length > 1 && (
              <div className="treatmentBlock treatmentBlock-list">
                <div className="treatmentBlock-subtitle">
                  {translate('immunotherapy.preparatoryCall.determineSequence')}
                </div>
                <div className="treatmentBlock medication select-medication-row">
                  <div className="medicationColumn treatmentBlock-text">
                    {translate(
                      'immunotherapy.preparatoryCall.medicationFor1stTimeIntake',
                    )}
                  </div>
                  <div className="medicationColumn">
                    <CustomSelect
                      emptyLabel={translate(
                        'immunotherapy.treatment.selectMedication',
                      )}
                      options={medicationOptions}
                      value={firstMedicationId}
                      onChange={handleFirstMedicationChange}
                      disabled={readonly}
                      error={!firstMedicationId}
                    />
                  </div>
                </div>
                <div className="treatmentBlock medication select-medication-row">
                  <div className="medicationColumn treatmentBlock-text">
                    {translate(
                      'immunotherapy.preparatoryCall.medicationFor2ndTimeIntake',
                    )}
                  </div>
                  <div className="medicationColumn">
                    <CustomSelect
                      emptyLabel={translate(
                        'immunotherapy.treatment.selectMedication',
                      )}
                      options={medicationOptions}
                      value={secondMedicationId}
                      onChange={handleSecondMedicationChange}
                      disabled={readonly}
                      error={!secondMedicationId}
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="treatmentBlock treatmentBlock-list">
              <div className="treatmentBlock-subtitle">
                {translate(
                  'immunotherapy.preparatoryCall.confirmPatientReceivedRelevantInformation',
                )}
              </div>
              <div className="preparatoryCall-checkbox-item">
                <CustomCheckbox
                  className="preparatoryCall-checkbox"
                  label=""
                  checked={treatmentProcessConfirmed}
                  onChange={(event) =>
                    setTreatmentProcessConfirmed(event.target.checked)
                  }
                  disabled={readonly}
                />
                <div className="preparatoryCall-checkbox-content">
                  <div className="preparatoryCall-checkbox-label">
                    {translate(
                      'immunotherapy.preparatoryCall.treatmentProcessLabel',
                    )}
                  </div>
                  <div className="preparatoryCall-checkbox-explanation">
                    {translate(
                      'immunotherapy.preparatoryCall.treatmentProcessExplanation',
                    )}
                  </div>
                </div>
              </div>
              <div className="preparatoryCall-checkbox-item">
                <CustomCheckbox
                  className="preparatoryCall-checkbox"
                  label=""
                  checked={sideEffectsConfirmed}
                  onChange={(event) =>
                    setSideEffectsConfirmed(event.target.checked)
                  }
                  disabled={readonly}
                />
                <div className="preparatoryCall-checkbox-content">
                  <div className="preparatoryCall-checkbox-label">
                    {translate(
                      'immunotherapy.preparatoryCall.possibleSideEffectsLabel',
                    )}
                  </div>
                  <div className="preparatoryCall-checkbox-explanation">
                    {translate(
                      'immunotherapy.preparatoryCall.possibleSideEffectsExplanation',
                    )}
                  </div>
                </div>
              </div>
              <div className="preparatoryCall-checkbox-item">
                <CustomCheckbox
                  className="preparatoryCall-checkbox"
                  label=""
                  checked={contraindicationsConfirmed}
                  onChange={(event) =>
                    setContraindicationsConfirmed(event.target.checked)
                  }
                  disabled={readonly}
                />
                <div className="preparatoryCall-checkbox-content">
                  <div className="preparatoryCall-checkbox-label">
                    {translate(
                      'immunotherapy.preparatoryCall.contraindicationsLabel',
                    )}
                  </div>
                  <div className="preparatoryCall-checkbox-explanation">
                    {translate(
                      'immunotherapy.preparatoryCall.contraindicationsExplanation',
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="treatmentBlock treatmentBlock-list">
              <div className="treatmentBlock-subtitle">
                {translate('immunotherapy.preparatoryCall.noteLabel')}
              </div>
              <CustomTextArea
                value={note}
                onChange={handleNoteChange}
                disabled={readonly}
              />
            </div>
            {formErrors.length > 0 && (
              <div className="formErrors">
                <ul>
                  {formErrors.map((error) => (
                    <li key={error} className="error">
                      * {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!readonly && (
              <div className="treatmentBlock">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24.5} />
                  ) : (
                    translate('immunotherapy.preparatoryCall.submitButtonText')
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </SimpleShowLayout>
  );
};

export default ImmunotherapyTreatmentPreparatoryCallForm;
